import React, { useCallback, useRef, useState } from 'react';

import { AgentProfileResponseDto } from '@shared/services/apiService';

import { RolePickerPopover } from './RolePickerPopover';
import { RolePickerTrigger } from './RolePickerTrigger';

interface RolePickerProps {
  profiles: AgentProfileResponseDto[] | undefined;
  selected: AgentProfileResponseDto | null | undefined;
  onChange: (profile: AgentProfileResponseDto) => void;
  disabled?: boolean;
}

export const RolePicker: React.FC<RolePickerProps> = ({
  profiles,
  selected,
  onChange,
  disabled,
}) => {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => {
    if (disabled) return;
    setOpen(true);
  }, [disabled]);

  const handleClose = useCallback(() => {
    setOpen(false);
    // Restore focus to the trigger so keyboard users keep their place.
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  }, []);

  const handleSelect = useCallback(
    (profile: AgentProfileResponseDto) => {
      onChange(profile);
    },
    [onChange],
  );

  return (
    <>
      <RolePickerTrigger
        ref={triggerRef}
        selected={selected ?? null}
        open={open}
        disabled={disabled}
        onClick={handleOpen}
      />
      <RolePickerPopover
        open={open}
        anchorEl={triggerRef.current}
        profiles={profiles}
        selectedId={selected?.id}
        onClose={handleClose}
        onSelect={handleSelect}
      />
    </>
  );
};

export type { RolePickerProps };
